import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
} from '@mui/material';
import { 
  Backup, 
  CloudDownload, 
  Delete,
  Storage,
  Build,
  Speed,
  Refresh,
  RestorePage,
  Info,
} from '@mui/icons-material';
import { useLanguage } from '../../../contexts/LanguageContext';
import config from '../../../config';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useConfirm } from '../../../hooks/useConfirm';
import BackupSchedulesSection from './BackupSchedulesSection';

interface BackupFile {
  fileName: string;
  fileSize: string;
  databaseName: string;
  createdAt: string;
}

interface FiscalDatabase {
  fiscalPeriodId: number;
  periodName: string;
  databaseName: string;
  startDate: string;
  endDate: string;
  isCurrent: boolean;
  isClosed: boolean;
}

const translations = {
  ar: {
    title: 'إعدادات قاعدة البيانات',
    subtitle: 'إدارة النسخ الاحتياطي والصيانة وأداء قاعدة البيانات',
    backupSettings: 'النسخ الاحتياطي',
    maintenance: 'الصيانة',
    performance: 'الأداء',
    databaseInfo: 'معلومات قاعدة البيانات',
    createBackup: 'إنشاء نسخة احتياطية',
    backupType: 'نوع النسخة',
    backupMain: 'القاعدة الرئيسية فقط',
    backupFiscal: 'سنة مالية محددة',
    backupAll: 'الكل (الرئيسية + جميع السنوات)',
    selectFiscalYear: 'اختر السنة المالية',
    restoreBackup: 'استعادة',
    downloadBackup: 'تحميل',
    deleteBackup: 'حذف',
    clearCache: 'مسح الذاكرة المؤقتة',
    maintenanceMode: 'وضع الصيانة',
    maintenanceWarning: 'وضع الصيانة مفعّل - لن يتمكن المستخدمون من الدخول للنظام',
    backupSuccess: 'تم إنشاء النسخة الاحتياطية بنجاح',
    backupError: 'فشل إنشاء النسخة الاحتياطية',
    restoreSuccess: 'تم استعادة قاعدة البيانات بنجاح',
    restoreError: 'فشل استعادة قاعدة البيانات',
    deleteSuccess: 'تم حذف النسخة الاحتياطية',
    clearSuccess: 'تم مسح الذاكرة المؤقتة بنجاح',
    clearError: 'فشل مسح الذاكرة المؤقتة',
    confirmClear: 'هل أنت متأكد من مسح الذاكرة المؤقتة؟',
    confirmRestore: 'تحذير: سيتم استبدال القاعدة الحالية بالكامل! هل أنت متأكد؟',
    confirmDelete: 'هل أنت متأكد من حذف هذه النسخة؟',
    noBackups: 'لا توجد نسخ احتياطية',
    fileName: 'اسم الملف',
    fileSize: 'الحجم',
    database: 'القاعدة',
    createdAt: 'تاريخ الإنشاء',
    actions: 'الإجراءات',
    refresh: 'تحديث',
    creating: 'جاري الإنشاء...',
    restoring: 'جاري الاستعادة...',
    dbName: 'اسم القاعدة',
    dbSize: 'حجم القاعدة',
    tablesCount: 'عدد الجداول',
    serverVersion: 'إصدار SQL Server',
    current: 'الحالية',
    closed: 'مقفلة',
    databases: 'قاعدة بيانات',
  },
  en: {
    title: 'Database Settings',
    subtitle: 'Manage backups, maintenance and database performance',
    backupSettings: 'Backup',
    maintenance: 'Maintenance',
    performance: 'Performance',
    databaseInfo: 'Database Info',
    createBackup: 'Create Backup',
    backupType: 'Backup Type',
    backupMain: 'Main Database Only',
    backupFiscal: 'Specific Fiscal Year',
    backupAll: 'All (Main + All Fiscal Years)',
    selectFiscalYear: 'Select Fiscal Year',
    restoreBackup: 'Restore',
    downloadBackup: 'Download',
    deleteBackup: 'Delete',
    clearCache: 'Clear Cache',
    maintenanceMode: 'Maintenance Mode',
    maintenanceWarning: 'Maintenance mode is active - users cannot access the system',
    backupSuccess: 'Backup created successfully',
    backupError: 'Failed to create backup',
    restoreSuccess: 'Database restored successfully',
    restoreError: 'Failed to restore database',
    deleteSuccess: 'Backup deleted successfully',
    clearSuccess: 'Cache cleared successfully',
    clearError: 'Failed to clear cache',
    confirmClear: 'Are you sure you want to clear the cache?',
    confirmRestore: 'WARNING: This will replace the entire current database! Are you sure?',
    confirmDelete: 'Are you sure you want to delete this backup?',
    noBackups: 'No backups found',
    fileName: 'File Name',
    fileSize: 'Size',
    database: 'Database',
    createdAt: 'Created At',
    actions: 'Actions',
    refresh: 'Refresh',
    creating: 'Creating...',
    restoring: 'Restoring...',
    dbName: 'Database Name',
    dbSize: 'Database Size',
    tablesCount: 'Tables Count',
    serverVersion: 'SQL Server Version',
    current: 'Current',
    closed: 'Closed',
    databases: 'database(s)',
  }
};

export default function DatabaseSettingsTab() {
  const { language } = useLanguage();
  const t = translations[language as keyof typeof translations];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [backups, setBackups] = useState<BackupFile[]>([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [dbInfo, setDbInfo] = useState<any>(null);
  const [fiscalDatabases, setFiscalDatabases] = useState<FiscalDatabase[]>([]);

  // Backup options
  const [backupType, setBackupType] = useState<'main' | 'fiscal' | 'all'>('main');
  const [selectedFiscalDb, setSelectedFiscalDb] = useState('');

  // Restore dialog
  const [restoreDialog, setRestoreDialog] = useState<{ open: boolean; fileName: string }>({ open: false, fileName: '' });
  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; fileName: string }>({ open: false, fileName: '' });

  const getToken = () => localStorage.getItem('token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    loadBackups();
    loadMaintenanceMode();
    loadDbInfo();
    loadFiscalDatabases();
  }, []);

  const loadFiscalDatabases = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/Database/fiscal-databases`, { headers: headers() });
      setFiscalDatabases(response.data.databases || []);
    } catch (err) {
      console.error('Failed to load fiscal databases:', err);
    }
  };

  const loadBackups = async () => {
    try {
      setBackupsLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/Database/backups`, { headers: headers() });
      setBackups(response.data.backups || []);
    } catch (err) {
      console.error('Failed to load backups:', err);
    } finally {
      setBackupsLoading(false);
    }
  };

  const loadMaintenanceMode = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/Database/maintenance`);
      setMaintenanceMode(response.data.maintenanceMode);
    } catch (err) {
      console.error('Failed to load maintenance mode:', err);
    }
  };

  const loadDbInfo = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/Database/info`, { headers: headers() });
      setDbInfo(response.data);
    } catch (err) {
      console.error('Failed to load db info:', err);
    }
  };

  const handleCreateBackup = async () => {
    if (backupType === 'fiscal' && !selectedFiscalDb) {
      toast.warning(isRTL ? 'اختر السنة المالية أولاً' : 'Select a fiscal year first');
      return;
    }

    try {
      setBackupLoading(true);
      const body: any = { backupType };
      if (backupType === 'fiscal') body.databaseName = selectedFiscalDb;

      const response = await axios.post(`${config.API_BASE_URL}/Database/backup`, body, { headers: headers() });
      const data = response.data;
      toast.success(`${isRTL ? data.messageAr : data.message} (${data.totalDatabases} ${t.databases})`);
      loadBackups();
    } catch (error: any) {
      toast.error(error.response?.data?.messageAr || error.response?.data?.message || t.backupError);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestore = (fileName: string) => {
    setRestoreDialog({ open: true, fileName });
  };

  const confirmRestore = async () => {
    try {
      setRestoreLoading(true);
      setRestoreDialog({ open: false, fileName: '' });
      await axios.post(`${config.API_BASE_URL}/Database/restore`, { fileName: restoreDialog.fileName }, { headers: headers() });
      toast.success(t.restoreSuccess);
      loadDbInfo();
    } catch (error: any) {
      toast.error(error.response?.data?.messageAr || error.response?.data?.message || t.restoreError);
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleDeleteBackup = async (fileName: string) => {
    setDeleteDialog({ open: true, fileName });
  };

  const confirmDelete = async () => {
    const fileName = deleteDialog.fileName;
    setDeleteDialog({ open: false, fileName: '' });
    try {
      await axios.delete(`${config.API_BASE_URL}/Database/backup/${fileName}`, { headers: headers() });
      toast.success(t.deleteSuccess);
      loadBackups();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete');
    }
  };

  const handleDownloadBackup = (fileName: string) => {
    const token = getToken();
    const url = `${config.API_BASE_URL}/Database/backup/download/${encodeURIComponent(fileName)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error(isRTL ? 'فشل تحميل الملف' : 'Failed to download file'));
  };

  const handleClearCache = async () => {
    if (!(await confirm({ message: t.confirmClear, severity: 'error' }))) return;
    try {
      setCacheLoading(true);
      await axios.post(`${config.API_BASE_URL}/Database/clear-cache`, {}, { headers: headers() });
      toast.success(t.clearSuccess);
    } catch (error: any) {
      toast.error(error.response?.data?.messageAr || t.clearError);
    } finally {
      setCacheLoading(false);
    }
  };

  const handleMaintenanceToggle = async (enabled: boolean) => {
    try {
      await axios.post(`${config.API_BASE_URL}/Database/maintenance`, { enabled }, { headers: headers() });
      setMaintenanceMode(enabled);
      toast.success(isRTL
        ? (enabled ? 'تم تفعيل وضع الصيانة' : 'تم إلغاء وضع الصيانة')
        : (enabled ? 'Maintenance mode enabled' : 'Maintenance mode disabled')
      );
    } catch (error: any) {
      toast.error(isRTL ? 'فشل تحديث وضع الصيانة' : 'Failed to update maintenance mode');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Color for database name chip
  const getDbColor = (dbName: string): 'primary' | 'secondary' | 'warning' | 'info' => {
    if (dbName === 'CountoraDB') return 'primary';
    return 'secondary';
  };

  return (
    <Box dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
          color: 'white', p: 4, borderRadius: 3, mb: 3,
          boxShadow: '0 8px 32px rgba(33, 147, 176, 0.3)'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Storage sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">{t.title}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{t.subtitle}</Typography>
          </Box>
        </Stack>
      </Box>

      <Stack spacing={3}>

        {/* Database Info Card */}
        {dbInfo && (
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' } }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                <Box sx={{ bgcolor: 'rgba(33, 147, 176, 0.1)', p: 1.5, borderRadius: 2 }}>
                  <Info sx={{ color: '#2193b0', fontSize: 28 }} />
                </Box>
                <Typography variant="h6" fontWeight="600">{t.databaseInfo}</Typography>
              </Stack>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                <Chip icon={<Storage />} label={`${t.dbName}: ${dbInfo.databaseName}`} variant="outlined" color="primary" />
                <Chip label={`${t.dbSize}: ${dbInfo.sizeMB}`} variant="outlined" color="info" />
                <Chip label={`${t.tablesCount}: ${dbInfo.tablesCount}`} variant="outlined" color="secondary" />
                <Chip label={`${t.serverVersion}: ${dbInfo.serverVersion}`} variant="outlined" />
                {fiscalDatabases.length > 0 && (
                  <Chip label={`${isRTL ? 'السنوات المالية' : 'Fiscal Years'}: ${fiscalDatabases.length}`} variant="outlined" color="warning" />
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Backup Card */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' } }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box sx={{ bgcolor: 'rgba(33, 147, 176, 0.1)', p: 1.5, borderRadius: 2 }}>
                <Backup sx={{ color: '#2193b0', fontSize: 28 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600">{t.backupSettings}</Typography>
              </Box>
              <Tooltip title={t.refresh}>
                <IconButton onClick={loadBackups} disabled={backupsLoading}><Refresh /></IconButton>
              </Tooltip>
            </Stack>

            {/* Backup Type Selection */}
            <Box sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: 'rgba(33, 147, 176, 0.04)', border: '1px solid rgba(33, 147, 176, 0.15)' }}>
              <Typography variant="subtitle2" fontWeight="600" mb={1.5} color="#2193b0">
                {t.backupType}
              </Typography>
              <RadioGroup
                value={backupType}
                onChange={(e) => {
                  setBackupType(e.target.value as 'main' | 'fiscal' | 'all');
                  setSelectedFiscalDb('');
                }}
              >
                <FormControlLabel value="main" control={<Radio size="small" />} label={
                  <Typography variant="body2" component="span">{t.backupMain} <Chip label="CountoraDB" size="small" color="primary" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} /></Typography>
                } />
                <FormControlLabel value="fiscal" control={<Radio size="small" />} label={
                  <Typography variant="body2" component="div">{t.backupFiscal}</Typography>
                } />
                <FormControlLabel value="all" control={<Radio size="small" />} label={
                  <Typography variant="body2" component="span">{t.backupAll} <Chip label={`${1 + fiscalDatabases.length} ${t.databases}`} size="small" color="warning" variant="outlined" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} /></Typography>
                } />
              </RadioGroup>

              {/* Fiscal Year Dropdown - only when "fiscal" selected */}
              {backupType === 'fiscal' && (
                <FormControl fullWidth size="small" sx={{ mt: 2 }}>
                  <InputLabel>{t.selectFiscalYear}</InputLabel>
                  <Select
                    value={selectedFiscalDb}
                    label={t.selectFiscalYear}
                    onChange={(e) => setSelectedFiscalDb(e.target.value)}
                  >
                    {fiscalDatabases.map((fd) => (
                      <MenuItem key={fd.databaseName} value={fd.databaseName}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <span>{fd.periodName} ({fd.databaseName})</span>
                          {fd.isCurrent && <Chip label={t.current} size="small" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />}
                          {fd.isClosed && <Chip label={t.closed} size="small" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>

            {/* Create Backup Button */}
            <Button
              fullWidth
              variant="contained"
              startIcon={backupLoading ? <CircularProgress size={20} color="inherit" /> : <Backup />}
              onClick={handleCreateBackup}
              disabled={backupLoading || restoreLoading}
              sx={{
                py: 1.5, mb: 3, borderRadius: 2,
                background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                textTransform: 'none', fontWeight: 600,
                '&:hover': { background: 'linear-gradient(135deg, #1a7a96 0%, #5ac0d4 100%)' }
              }}
            >
              {backupLoading ? t.creating : t.createBackup}
            </Button>

            <Divider sx={{ mb: 2 }} />

            {/* Backups List */}
            {backupsLoading ? (
              <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
            ) : backups.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={3}>{t.noBackups}</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: 'rgba(33, 147, 176, 0.08)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.fileName}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.database}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.fileSize}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>{t.createdAt}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>{t.actions}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backups.map((backup) => (
                      <TableRow key={backup.fileName} hover>
                        <TableCell>
                          <Typography variant="body2" component="div" fontWeight="500" sx={{ fontSize: '0.8rem' }}>{backup.fileName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={backup.databaseName} size="small" color={getDbColor(backup.databaseName)} variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />
                        </TableCell>
                        <TableCell>
                          <Chip label={backup.fileSize} size="small" color="info" variant="outlined" sx={{ height: 22, fontSize: '0.72rem' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>{formatDate(backup.createdAt)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title={t.downloadBackup}>
                              <IconButton size="small" color="success" onClick={() => handleDownloadBackup(backup.fileName)}>
                                <CloudDownload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.restoreBackup}>
                              <IconButton size="small" color="primary" onClick={() => handleRestore(backup.fileName)} disabled={restoreLoading}>
                                <RestorePage fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t.deleteBackup}>
                              <IconButton size="small" color="error" onClick={() => handleDeleteBackup(backup.fileName)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {restoreLoading && (
              <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={20} />
                  <Typography>{t.restoring}</Typography>
                </Stack>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Maintenance Card */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', border: maintenanceMode ? '2px solid #ff9800' : '1px solid #e0e0e0', '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' } }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box sx={{ bgcolor: maintenanceMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(109, 213, 237, 0.1)', p: 1.5, borderRadius: 2 }}>
                <Build sx={{ color: maintenanceMode ? '#ff9800' : '#6dd5ed', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">{t.maintenance}</Typography>
            </Stack>

            <Stack spacing={2}>
              <FormControlLabel
                control={<Switch checked={maintenanceMode} onChange={(e) => handleMaintenanceToggle(e.target.checked)} color="warning" />}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Build sx={{ fontSize: 20, color: maintenanceMode ? '#ff9800' : '#bbb' }} />
                    <span>{t.maintenanceMode}</span>
                  </Stack>
                }
              />
              {maintenanceMode && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>{t.maintenanceWarning}</Alert>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Performance Card */}
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', transition: 'all 0.3s ease', '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' } }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box sx={{ bgcolor: 'rgba(33, 147, 176, 0.1)', p: 1.5, borderRadius: 2 }}>
                <Speed sx={{ color: '#2193b0', fontSize: 28 }} />
              </Box>
              <Typography variant="h6" fontWeight="600">{t.performance}</Typography>
            </Stack>

            <Button
              fullWidth variant="outlined" color="error"
              startIcon={cacheLoading ? <CircularProgress size={20} /> : <Delete />}
              onClick={handleClearCache} disabled={cacheLoading}
              sx={{ py: 1.5, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              {t.clearCache}
            </Button>
          </CardContent>
        </Card>
      </Stack>

      {/* Scheduled Backups Section */}
      <BackupSchedulesSection fiscalDatabases={fiscalDatabases} onRefreshBackups={loadBackups} />

      {/* Restore Confirmation Dialog */}
      <Dialog 
        open={restoreDialog.open} 
        onClose={() => setRestoreDialog({ open: false, fileName: '' })}
        PaperProps={{
          sx: {
            borderRadius: 3, minWidth: 420,
            border: '2px solid rgba(255, 152, 0, 0.3)',
            boxShadow: '0 8px 32px rgba(255, 152, 0, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: 'rgba(255, 152, 0, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <RestorePage sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold" color="#ff9800">
              {isRTL ? 'تأكيد استعادة قاعدة البيانات' : 'Confirm Database Restore'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{t.confirmRestore}</Alert>
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.04)', border: '1px solid rgba(255, 152, 0, 0.15)' }}>
            <Typography variant="body2" color="text.secondary">{isRTL ? 'الملف:' : 'File:'}</Typography>
            <Typography variant="body1" fontWeight="600" sx={{ mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {restoreDialog.fileName}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setRestoreDialog({ open: false, fileName: '' })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            variant="contained" color="warning" startIcon={<RestorePage />}
            onClick={confirmRestore}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isRTL ? 'تأكيد الاستعادة' : 'Confirm Restore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, fileName: '' })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 420,
            border: '2px solid rgba(244, 67, 54, 0.3)',
            boxShadow: '0 8px 32px rgba(244, 67, 54, 0.15)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 48, height: 48, borderRadius: '50%',
              bgcolor: 'rgba(244, 67, 54, 0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Delete sx={{ color: '#f44336', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" fontWeight="bold" color="#f44336">
              {isRTL ? 'تأكيد حذف النسخة الاحتياطية' : 'Confirm Delete Backup'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
            {isRTL 
              ? 'سيتم حذف هذه النسخة الاحتياطية نهائياً ولا يمكن استرجاعها!'
              : 'This backup will be permanently deleted and cannot be recovered!'}
          </Alert>
          <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.04)', border: '1px solid rgba(244, 67, 54, 0.15)' }}>
            <Typography variant="body2" color="text.secondary">
              {isRTL ? 'الملف:' : 'File:'}
            </Typography>
            <Typography variant="body1" fontWeight="600" sx={{ mt: 0.5, fontFamily: 'monospace', wordBreak: 'break-all' }}>
              {deleteDialog.fileName}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button 
            onClick={() => setDeleteDialog({ open: false, fileName: '' })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isRTL ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            startIcon={<Delete />}
            onClick={confirmDelete}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {isRTL ? 'حذف نهائي' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}